import { MenuItemInterface } from '@/interfaces/menu'; // Assuming NavigationItem is compatible with MenuItemInterface or similar
// Assuming Role is needed, based on convertToNavigationItem usage

// Placeholder for the actual type if different from MenuItemInterface
// It might be defined in '../config/navigations' or related files.
// Let's assume it's similar to MenuItemInterface for now.
// If you have a specific type for the converted items, replace NavigationItem here.
export interface NavigationItem extends MenuItemInterface {
  route?: string;
  children?: NavigationItem[];
}

/**
 * Filters menu items based on a list of deprecated routes.
 *
 * Rules:
 * 1. If a deprecated route matches a root node with no children, filter out the node.
 * 2. If a deprecated route matches a root node with children:
 *    - Remove the child with the matching route (if any).
 *    - Update the root node's route to be the next available child's route.
 *    - If no children remain after filtering, remove the root node.
 * 3. If a deprecated route matches a child node but not the root's route, remove the matching child.
 *
 * @param menuItems The list of navigation items to filter. It might be undefined.
 * @param deprecatedRoutes A list of route strings to deprecate.
 * @returns The filtered list of navigation items.
 */
export function filterDeprecatedMenuItems(
  menuItems: NavigationItem[] | undefined,
  deprecatedRoutes: string[],
): NavigationItem[] {
  // Return empty array if input is undefined or null
  if (!menuItems) {
    return [];
  }

  const deprecatedSet = new Set(deprecatedRoutes);

  const filteredItems = menuItems
    .map((item) => {
      // Clone to avoid direct mutation of the original objects from Redux store or props
      // Use shallow clone + separate children clone to preserve React elements (like icons)
      const newItem = { ...item };

      // Filter children first - also use shallow cloning for children
      if (newItem.children && newItem.children.length > 0) {
        newItem.children = newItem.children
          .map((child) => ({ ...child })) // Shallow clone each child
          .filter((child) => !deprecatedSet.has(child.route ?? ''));
      }

      const isRootDeprecated = deprecatedSet.has(newItem.route ?? '');
      const hasChildren = newItem.children && newItem.children.length > 0;

      // Rule 2: Root is deprecated, but has children left after filtering
      if (isRootDeprecated && hasChildren) {
        // Update the root route to the first remaining child's route
        newItem.route = newItem.children![0].route;
        // Keep the item since it still has children
        return newItem;
      }

      // Rule 1 & 2 (cont.): Root is deprecated, and has no children (either initially or after filtering)
      if (isRootDeprecated && !hasChildren) {
        return null; // Remove the root node
      }

      // Rule 3 (cont.): Root is NOT deprecated, but all children were removed
      // If the requirement is to remove root nodes if ALL children are removed, even if root wasn't deprecated:
      // if (!isRootDeprecated && newItem.children && newItem.children.length === 0) {
      //    return null;
      // }
      // If we keep the root node in this case:
      if (
        !isRootDeprecated &&
        newItem.children &&
        newItem.children.length === 0
      ) {
        newItem.children = []; // Ensure children is an empty array
      }

      // If the item itself wasn't deprecated and passed previous checks
      return newItem;
    })
    .filter((item): item is NavigationItem => item !== null); // Remove null entries

  return filteredItems;
}
